import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getFilteredClasses, getArchivedClasses, markClassCompleted } from "../api/Classes";
import { Class, LicState } from "../types";
import { useAuth } from '../useAuth';
import { getCyclesForUser, Cycle } from "../api/Cycles";
import { getStatesForUser } from "../api/States";
import { getAllProfessions, Profession } from "../api/Professions";
import { formatCalendarDate } from "../utils/timezone";

interface Stats {
  totalClasses: number;
  completedClasses: number;
  pendingClasses: number;
  totalCredits: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function Classes() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [classes, setClasses] = useState<Class[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [userStates, setUserStates] = useState<LicState[]>([]);
  const [userProfession, setUserProfession] = useState<Profession | null>(null);
  const [creditTerm, setCreditTerm] = useState('CEC'); // Default credit terminology
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Stats state
  const [stats, setStats] = useState<Stats>({
    totalClasses: 0,
    completedClasses: 0,
    pendingClasses: 0,
    totalCredits: 0
  });
  
  // Pagination state
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  
  // Filter state
  const [search, setSearch] = useState("");
  const [filterCycle, setFilterCycle] = useState("");
  const [filterCompleted, setFilterCompleted] = useState("");
  const [filterState, setFilterState] = useState("");
  const [sortBy, setSortBy] = useState<"classname" | "classdate" | "classLU" | "took">("classdate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [noCycleBannerDismissed, setNoCycleBannerDismissed] = useState(false);
  const [badDateBannerDismissed, setBadDateBannerDismissed] = useState(false);

  // Load dropdown data on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      if (!user) return;
      try {
        const [cyclesData, statesData, professionsData] = await Promise.all([
          getCyclesForUser(),
          getStatesForUser(user.id),
          getAllProfessions(),
        ]);
        
        // Sort cycles by end date descending
        const sortedCycles = cyclesData.sort((a, b) => {
          const dateA = new Date(a.cycleend);
          const dateB = new Date(b.cycleend);
          if (dateB.getTime() !== dateA.getTime()) {
            return dateB.getTime() - dateA.getTime();
          }
          const stateA = a.state || '';
          const stateB = b.state || '';
          return stateA.localeCompare(stateB);
        });
        
        setCycles(sortedCycles);
        setUserStates(statesData);
        
        // Set credit terminology based on user's profession
        if (user.professionID) {
          const profession = professionsData.find(p => p.professionID === user.professionID);
          if (profession) {
            setUserProfession(profession);
            setCreditTerm(profession.creditTerminology);
          }
        }
      } catch (err) {
        console.error("Error loading dropdown data:", err);
      }
    };
    loadDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Use user?.id instead of user object to prevent infinite loop

  // Load classes whenever filters change
  useEffect(() => {
    if (user) {
      loadClasses();
    }
  }, [user, search, filterCycle, filterCompleted, filterState, sortBy, sortOrder, pagination.page, pagination.limit]);

  const loadClasses = async () => {
    if (!user) return;
    setLoading(true);
    try {
      if (filterCompleted === "archived") {
        const data = await getArchivedClasses();
        const filtered = search
          ? data.filter(c => c.classname?.toLowerCase().includes(search.toLowerCase()))
          : data;
        setClasses(filtered);
        setPagination({ page: 1, limit: filtered.length, total: filtered.length, totalPages: 1 });
        return;
      }

      // Convert cycle ObjectId to numeric cycleID if needed
      let cycleID: number | undefined;
      if (filterCycle) {
        const selectedCycle = cycles.find(c => c._id === filterCycle);
        if (selectedCycle && selectedCycle.cycleid !== undefined) {
          cycleID = selectedCycle.cycleid;
        }
      }

      // Convert state to stateID if needed
      const stateID = filterState ? parseInt(filterState) : undefined;

      // Convert took filter
      let took: boolean | undefined;
      if (filterCompleted === "yes") took = true;
      if (filterCompleted === "no") took = false;

      const response = await getFilteredClasses({
        search: search || undefined,
        took,
        cycleID,
        stateID,
        sortBy,
        sortOrder,
        page: pagination.page,
        limit: pagination.limit,
        userId: user.id
      });

      setClasses(response.classes);
      setStats(response.stats);
      setPagination(response.pagination);
    } catch (err) {
      console.error("Error loading classes:", err);
      setError("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (classId: string) => {
    try {
      if (!user?.id) {
        setError('You must be logged in to complete a class.');
        return;
      }
      await markClassCompleted(classId, user.id);
      // Refresh to update stats and status
      loadClasses();
    } catch (err: any) {
      const serverMsg = err.response?.data?.message || err.response?.data?.error;
      setError(serverMsg || 'Failed to mark class as completed.');
      console.error("Error marking class completed:", err);
    }
  };

  const handleSort = (field: "classname" | "classdate" | "classLU" | "took") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({ ...pagination, limit: newLimit, page: 1 });
  };

  const clearFilters = () => {
    setSearch("");
    setFilterCycle("");
    setFilterCompleted("");
    setFilterState("");
    setSortBy("classdate");
    setSortOrder("desc");
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportToCSV = () => {
    const headers = ["Class Name", "Date", "Credits", "Status", "Has Certificate"];
    const rows = classes.map(c => [
      c.classname,
      formatCalendarDate(c.classdate),
      c.classLU.toString(),
      c.took ? "Completed" : "Pending",
      c.docpath ? "Yes" : "No"
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `classes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Aggregate cycle date window — used to qualify "no cycle" badge
  const minCycleTs = cycles.length > 0 ? Math.min(...cycles.map(c => new Date(c.cyclestart).getTime())) : null;
  const maxCycleTs = cycles.length > 0 ? Math.max(...cycles.map(c => new Date(c.cycleend).getTime())) : null;

  const isUnassignedInWindow = (cls: Class): boolean => {
    if (!minCycleTs || !maxCycleTs) return false;
    const t = new Date(cls.classdate).getTime();
    return (!cls.cycleIDs || cls.cycleIDs.length === 0) && t >= minCycleTs && t <= maxCycleTs;
  };

  const unassignedInWindowCount = classes.filter(isUnassignedInWindow).length;

  const isBadYear = (cls: Class): boolean => {
    const yr = new Date(cls.classdate).getUTCFullYear();
    return yr < 1970 || yr > new Date().getFullYear() + 2;
  };
  const badDateCount = classes.filter(isBadYear).length;

  if (loading) return <div className="container mt-4"><div className="text-center"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div></div>;

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <h2 className="mb-0">Classes</h2>
        {user && (
          <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-sm-auto">
            <Link to="/archived-classes" className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-archive me-1"></i>View Archived
            </Link>
            <Link to="/deleted-classes" className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-trash me-1"></i>View Deleted
            </Link>
            <button onClick={exportToCSV} className="btn btn-outline-secondary btn-sm">
              ▤ Export CSV
            </button>
            <Link to="/classes/import" className="btn btn-outline-primary btn-sm">
              📥 <span className="d-none d-sm-inline">Bulk </span>Import
            </Link>
            <Link to="/add-class?returnTo=/classes" className="btn btn-primary btn-sm">
              + <span className="d-none d-sm-inline">Add New </span>Class
            </Link>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger alert-dismissible fade show" role="alert">
        {error}
        <button type="button" className="btn-close" onClick={() => setError('')}></button>
      </div>}

      {!noCycleBannerDismissed && unassignedInWindowCount > 0 && filterCompleted !== 'archived' && (
        <div className="alert alert-warning alert-dismissible d-flex align-items-start gap-2 mb-4" role="alert">
          <i className="bi bi-exclamation-circle-fill mt-1 flex-shrink-0"></i>
          <div>
            <strong>{unassignedInWindowCount} {unassignedInWindowCount === 1 ? 'class' : 'classes'} on this page {unassignedInWindowCount === 1 ? 'is' : 'are'} not assigned to a cycle</strong> and won't count toward your compliance totals.
            {' '}Open each class and assign it to a cycle to have it count.
            {filterCycle === '' && <span className="ms-1">Use the <strong>Cycle</strong> filter above to show only unassigned classes.</span>}
          </div>
          <button type="button" className="btn-close ms-auto" onClick={() => setNoCycleBannerDismissed(true)} aria-label="Dismiss"></button>
        </div>
      )}

      {!badDateBannerDismissed && badDateCount > 0 && (
        <div className="alert alert-danger alert-dismissible d-flex align-items-start gap-2 mb-4" role="alert">
          <i className="bi bi-calendar-x-fill mt-1 flex-shrink-0"></i>
          <div>
            <strong>{badDateCount} {badDateCount === 1 ? 'class' : 'classes'} on this page {badDateCount === 1 ? 'has' : 'have'} an incorrect date</strong> — the year looks like a typo (e.g. "203" instead of "2023").
            {' '}Click the class name to open it and correct the date.
          </div>
          <button type="button" className="btn-close ms-auto" onClick={() => setBadDateBannerDismissed(true)} aria-label="Dismiss"></button>
        </div>
      )}

      {/* Stats Cards — hidden when viewing archived (stats reflect active classes only) */}
      {filterCompleted !== "archived" && <div className="row g-3 mb-4">
        {[
          { label: 'Total Classes', value: stats.totalClasses,     colorClass: 'cec-card-navy'  },
          { label: 'Completed',     value: stats.completedClasses, colorClass: 'cec-card-green' },
          { label: 'Pending',       value: stats.pendingClasses,   colorClass: 'cec-card-amber', title: 'Count of classes marked "not completed," including any tied to cycles that have since ended. This may be higher than the Dashboard\'s "Pending Classes" widget, which only lists classes that still fall within a currently open cycle.' },
          { label: 'Total Credits', value: stats.totalCredits,     colorClass: 'cec-card-cyan'  },
        ].map(({ label, value, colorClass, title }) => (
          <div key={label} className="col-6 col-md-3">
            <div className={`card cec-stat-card ${colorClass}`} title={title}>
              <div className="card-body p-3">
                <div className="cec-stat-label">{label}</div>
                <div className="cec-stat-value">{value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label htmlFor="search" className="form-label small">Search by Name</label>
              <input
                id="search"
                type="text"
                className="form-control form-control-sm"
                placeholder="Search classes..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            <div className="col-6 col-md-3">
              <label htmlFor="cycle-filter" className="form-label small">Cycle</label>
              <select 
                id="cycle-filter" 
                className="form-select form-select-sm" 
                value={filterCycle} 
                onChange={(e) => {
                  setFilterCycle(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All Cycles</option>
                {cycles.map((c: Cycle) => (
                  <option key={c._id} value={c._id}>{c.cyclename}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label htmlFor="state-filter" className="form-label small">State</label>
              <select
                id="state-filter"
                className="form-select form-select-sm"
                value={filterState}
                onChange={(e) => {
                  setFilterState(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All States</option>
                {userStates.map((s: LicState) => (
                  <option key={s.stateID} value={s.stateID}>{s.state}</option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4">
              <label htmlFor="completed-filter" className="form-label small">Status</label>
              <select 
                id="completed-filter" 
                className="form-select form-select-sm" 
                value={filterCompleted} 
                onChange={(e) => {
                  setFilterCompleted(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">All</option>
                <option value="yes">Completed</option>
                <option value="no">Pending</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="col-6 col-md-4">
              <label htmlFor="sort-by" className="form-label small">Sort By</label>
              <select 
                id="sort-by" 
                className="form-select form-select-sm" 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="classdate">Date</option>
                <option value="classname">Name</option>
                <option value="classLU">Credits</option>
                <option value="took">Status</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label htmlFor="sort-order" className="form-label small">Order</label>
              <select 
                id="sort-order" 
                className="form-select form-select-sm" 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value as any)}
              >
                <option value="asc">↑ Asc</option>
                <option value="desc">↓ Desc</option>
              </select>
            </div>
            <div className="col-6 col-md-2 d-flex align-items-end">
              <button onClick={clearFilters} className="btn btn-outline-secondary btn-sm w-100">
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">
          {filterCompleted === "archived"
            ? `Showing ${pagination.total} archived ${pagination.total === 1 ? 'class' : 'classes'}`
            : `Showing ${pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit + 1)} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} classes`
          }
        </div>
        {filterCompleted !== "archived" && (
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="limit-select" className="small mb-0">Rows:</label>
            <select
              id="limit-select"
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={pagination.limit}
              onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        )}
      </div>

      {/* Classes Table */}
      <div className="table-responsive">
        <table className="table cec-table table-hover table-sm">
          <thead>
            <tr>
              <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort("classname")}>
                Name {sortBy === "classname" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th scope="col" className="d-none d-md-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleSort("classdate")}>
                Date {sortBy === "classdate" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th scope="col" style={{ cursor: 'pointer' }} onClick={() => handleSort("classLU")}>
                {creditTerm} {sortBy === "classLU" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th scope="col" className="d-none d-lg-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleSort("took")}>
                Status {sortBy === "took" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {classes.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  {loading ? "Loading..." : "No classes found"}
                </td>
              </tr>
            ) : (
              classes.map((c: Class) => {
                const displayName = c.classname.length > 45 
                  ? c.classname.substring(0, 45) + '…' 
                  : c.classname;
                
                return (
                  <tr
                    key={c._id}
                    onClick={() => navigate(`/classes/${c._id}?returnTo=/classes`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/classes/${c._id}?returnTo=/classes`);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    style={{ cursor: 'pointer' }}
                  >
                    <td title={c.classname} style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center gap-1">
                          <Link
                            to={`/classes/${c._id}?returnTo=/classes`}
                            className="class-name-link"
                            style={{ fontWeight: 500 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {displayName}
                          </Link>
                          {c.docpath && <span className="badge bg-info text-white" title="Has certificate">▣</span>}
                          {isUnassignedInWindow(c) && (
                            <span className="badge text-white" style={{ background: '#b45309' }} title="Not assigned to any cycle — won't count toward compliance">No cycle</span>
                          )}
                        </div>
                        <small className="text-muted d-md-none">{formatCalendarDate(c.classdate)}</small>
                      </div>
                    </td>
                    <td className="d-none d-md-table-cell">
                      {(() => {
                        const d = new Date(c.classdate);
                        const yr = d.getUTCFullYear();
                        const badYear = yr < 1970 || yr > new Date().getFullYear() + 2;
                        return badYear
                          ? <span className="text-danger" title={`Year "${yr}" looks incorrect — click the class to fix the date`}>⚠ {formatCalendarDate(c.classdate)}</span>
                          : formatCalendarDate(c.classdate);
                      })()}
                    </td>
                    <td>{c.classLU}</td>
                    <td className="d-none d-lg-table-cell">
                      {c.took ?
                        <span className="badge bg-success">Completed</span> :
                        <span className="badge bg-warning text-dark">Pending</span>
                      }
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <nav aria-label="Classes pagination">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
            </li>
            
            {/* Page numbers */}
            {(() => {
              const pages = [];
              const maxVisible = 5;
              let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
              let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
              
              if (endPage - startPage < maxVisible - 1) {
                startPage = Math.max(1, endPage - maxVisible + 1);
              }
              
              if (startPage > 1) {
                pages.push(
                  <li key="page-1" className="page-item">
                    <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                  </li>
                );
                if (startPage > 2) {
                  pages.push(<li key="ellipsis-1" className="page-item disabled"><span className="page-link">...</span></li>);
                }
              }
              
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <li key={`page-${i}`} className={`page-item ${pagination.page === i ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                  </li>
                );
              }
              
              if (endPage < pagination.totalPages) {
                if (endPage < pagination.totalPages - 1) {
                  pages.push(<li key="ellipsis-2" className="page-item disabled"><span className="page-link">...</span></li>);
                }
                pages.push(
                  <li key={`page-${pagination.totalPages}`} className="page-item">
                    <button className="page-link" onClick={() => handlePageChange(pagination.totalPages)}>{pagination.totalPages}</button>
                  </li>
                );
              }
              
              return pages;
            })()}
            
            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

export default Classes;
